import React from "react";
import "../../styles/gateway-section.css";
import {
  exclusiveSkipsConvergence,
  EMPTY_CONVERGE_FIELDS,
  gatewayRequiresConvergence,
} from "../../utils/gatewayConvergence";

/**
 * GatewaySection - A user-friendly gateway configuration component
 * Features:
 * - Visual gateway type cards with clear explanations
 * - Step-by-step guided configuration
 * - Common pattern templates
 */

const GATEWAY_TYPES = [
  {
    id: "exclusive",
    value: "EXCLUSIVE",
    label: "Exclusive",
    shortLabel: "XOR",
    symbol: "✕",
    color: "#ef4444",
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
    description: "Only ONE path executes",
    example: "If approved → continue, else → reject",
  },
  {
    id: "parallel",
    value: "PARALLEL",
    label: "Parallel",
    shortLabel: "AND",
    symbol: "+",
    color: "#22c55e",
    bgColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    description: "ALL paths execute at once",
    example: "Do A and B simultaneously, wait for both",
  },
  {
    id: "inclusive",
    value: "INCLUSIVE",
    label: "Inclusive",
    shortLabel: "OR",
    symbol: "○",
    color: "#f59e0b",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
    description: "One or MORE paths execute",
    example: "If needs A → do A, if needs B → do B (can do both)",
  },
  {
    id: "event_based",
    value: "EVENT_BASED",
    label: "Event-Based",
    shortLabel: "EVT",
    symbol: "⬡",
    color: "#8b5cf6",
    bgColor: "#f5f3ff",
    borderColor: "#ddd6fe",
    description: "Wait for external event",
    example: "Wait for response OR timeout",
  },
  {
    id: "complex",
    value: "COMPLEX",
    label: "Complex",
    shortLabel: "*",
    symbol: "✱",
    color: "#9b59b6",
    bgColor: "#f5f3ff",
    borderColor: "#e9d5ff",
    description: "Complex gateway with custom evaluation semantics",
    example: "Custom multi-condition routing or advanced merging",
  },
];

const QUICK_PATTERNS = [
  {
    id: "simple-decision",
    name: "Simple Yes/No Decision",
    description: "Two paths: approved or rejected",
    type: "exclusive",
    icon: "✓✕",
    branches: 2,
    hasEndpoint: true,
  },
  {
    id: "three-way",
    name: "Three-Way Split",
    description: "Route to one of three paths",
    type: "exclusive",
    icon: "⋔",
    branches: 3,
  },
  {
    id: "parallel-work",
    name: "Parallel Work",
    description: "Do multiple things at once",
    type: "parallel",
    icon: "⇉",
    branches: 2,
    needsConverge: true,
  },
  {
    id: "approval-loop",
    name: "Approval with Revision",
    description: "Approve, revise, or reject",
    type: "exclusive",
    icon: "↻",
    branches: 2,
    hasEndpoint: true,
  },
];

const createId = () =>
  `gw-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
const START_EVENT_ID = "__START_EVENT__";
const START_EVENT_OPTION = {
  id: START_EVENT_ID,
  label: "Start Event",
  order: 0,
  isStartEvent: true,
};

function GatewaySection({
  gateways = [],
  onGatewaysChange,
  workflowTasks = [],
  showPreview = false,
  onPreviewRequest,
}) {
  const [expandedGateway, setExpandedGateway] = React.useState(null);
  const [showAddPanel, setShowAddPanel] = React.useState(false);
  const [configStep, setConfigStep] = React.useState(1);
  const [newGateway, setNewGateway] = React.useState(null);

  // Get task / subprocess options for dropdowns
  const taskOptions = React.useMemo(() => {
    return workflowTasks.map((t, idx) => {
      const isSubProcess = !!(t.isSubProcess || t.is_sub_process || t.isChildProcess);
      const baseLabel =
        t.task_name || t.taskName || t.name || `Task ${idx + 1}`;
      return {
        id: String(t.task_id || t.taskId || t.id),
        label:
          isSubProcess && !String(baseLabel).includes("(Subprocess)")
            ? `${baseLabel} (Subprocess)`
            : baseLabel,
        order: t.order ?? idx + 1,
        isSubProcess,
      };
    });
  }, [workflowTasks]);

  // Other gateways as placement targets: a gateway can be placed directly after
  // another gateway (gateway -> gateway). Encoded as "__GW__<local id>".
  const gatewayPlacementOptions = React.useMemo(
    () =>
      (gateways || []).map((g, idx) => ({
        id: `__GW__${g.id}`,
        label: `Gateway: ${g.name || g.type || `Gateway ${idx + 1}`}`,
        isGateway: true,
      })),
    [gateways],
  );

  const placementOptions = React.useMemo(
    () => [START_EVENT_OPTION, ...taskOptions, ...gatewayPlacementOptions],
    [taskOptions, gatewayPlacementOptions],
  );

  // Per-gateway placement options exclude the gateway itself (can't follow itself).
  const getPlacementOptionsFor = (gateway) =>
    placementOptions.filter((o) => o.id !== `__GW__${gateway?.id}`);

  const getPlacementLabel = (afterTaskId) => {
    if (String(afterTaskId) === START_EVENT_ID) return START_EVENT_OPTION.label;
    const s = String(afterTaskId ?? "");
    if (s.startsWith("__GW__")) {
      const g = (gateways || []).find((x) => `__GW__${x.id}` === s);
      return g ? `Gateway: ${g.name || g.type}` : "Gateway";
    }
    return taskOptions.find((t) => t.id === s)?.label || "Not selected";
  };

  // Get tasks that come after a specific task
  const getTasksAfter = (afterTaskId) => {
    if (!afterTaskId || String(afterTaskId) === START_EVENT_ID)
      return taskOptions;
    const afterIdx = taskOptions.findIndex((t) => t.id === String(afterTaskId));
    if (afterIdx === -1) return taskOptions;
    // Allow all tasks for loop-back scenarios
    return taskOptions;
  };

  // Use the same task options as branch targets for convergence selection.
  const getConvergeOptions = (gateway) => {
    return getTasksAfter(gateway.afterTaskId);
  };

  // Best-guess join point for a gateway that needs one but doesn't have it
  // set yet: the task right after the furthest-along branch target, so every
  // branch (including shorter ones) has somewhere to land. Falls back to End
  // when no branch continues onto the workflow.
  const suggestConvergeTarget = (gateway) => {
    const continuingOrders = (gateway.branches || [])
      .map((b) => b.targetTaskId)
      .filter((t) => t && !(typeof t === "string" && t.startsWith("__GW__")))
      .map((t) => taskOptions.find((o) => String(o.id) === String(t))?.order)
      .filter((o) => Number.isFinite(o));
    if (continuingOrders.length === 0) return "__END__";
    const maxOrder = Math.max(...continuingOrders);
    const next = taskOptions.find((o) => o.order === maxOrder + 1);
    return next ? String(next.id) : "__END__";
  };

  // Helpers for normalizing and resolving types (accept both lowercase and UPPERCASE)
  const normalizeType = (t) => (t || "").toString().toUpperCase();

  const getTypeInfo = (type) => {
    const n = normalizeType(type);
    return (
      GATEWAY_TYPES.find(
        (t) =>
          (t.value && normalizeType(t.value) === n) ||
          String(t.id).toUpperCase() === n,
      ) || GATEWAY_TYPES[0]
    );
  };

  // Start adding a new gateway
  const startAddGateway = (pattern = null) => {
    const base = {
      id: createId(),
      type: pattern?.type ? pattern.type.toString().toUpperCase() : "EXCLUSIVE",
      name: "",
      afterTaskId: START_EVENT_ID,
      convergeAtTaskId: "",
      convergeGatewayName: "",
      convergeToEnd: false,
      convergeAtGatewayId: "",
      branches: [],
      endpoints: [],
    };

    if (pattern) {
      const branchCount = pattern.branches || 2;
      for (let i = 0; i < branchCount; i++) {
        base.branches.push({
          id: createId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          connectToEnd: false,
        });
      }
      if (pattern.hasEndpoint) {
        base.endpoints = [
          {
            id: createId(),
            afterTaskId: "",
            endEventName: "Rejected",
            condition: "",
          },
        ];
      }
    } else {
      base.branches = [
        {
          id: createId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          connectToEnd: false,
        },
        {
          id: createId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          connectToEnd: false,
        },
      ];
    }

    setNewGateway(base);
    setConfigStep(1);
    setShowAddPanel(true);
  };

  const updateNewGateway = (field, value) => {
    setNewGateway((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "type") {
        const n = normalizeType(value);
        if (n === "EXCLUSIVE" || n === "EVENT_BASED") {
          updated.convergeAtTaskId = "";
          updated.convergeGatewayName = "";
        }
        if (n === "PARALLEL") {
          updated.endpoints = [];
        }
        // store normalized value
        updated.type = n;
      }
      return updated;
    });
  };

  const addBranchToNew = () => {
    setNewGateway((prev) => ({
      ...prev,
      branches: [
        ...prev.branches,
        {
          id: createId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          connectToEnd: false,
        },
      ],
    }));
  };

  // A gateway needs at least 2 outgoing paths. Named End Events (endpoints)
  // count as paths just like branches, so each end event reduces the number of
  // branches required. (Parallel gateways have no end events, so this stays 2.)
  const minBranchCount = (gw) => {
    const endpointCount = (gw?.endpoints || []).length;
    return Math.max(2 - endpointCount, 0);
  };

  const removeBranchFromNew = (branchId) => {
    if (newGateway.branches.length <= minBranchCount(newGateway)) return;
    setNewGateway((prev) => ({
      ...prev,
      branches: prev.branches.filter((b) => b.id !== branchId),
    }));
  };

  const updateNewBranch = (branchId, field, value) => {
    setNewGateway((prev) => {
      const updated = {
        ...prev,
        branches: prev.branches.map((b) =>
          b.id === branchId ? { ...b, [field]: value } : b,
        ),
      };
      return exclusiveSkipsConvergence(updated)
        ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
        : updated;
    });
  };

  const addEndpointToNew = () => {
    setNewGateway((prev) => {
      const updated = {
        ...prev,
        endpoints: [
          ...(prev.endpoints || []),
          { id: createId(), endTaskId: "", endEventName: "", condition: "" },
        ],
      };
      return exclusiveSkipsConvergence(updated)
        ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
        : updated;
    });
  };

  const removeEndpointFromNew = (endpointId) => {
    setNewGateway((prev) => {
      const updated = {
        ...prev,
        endpoints: (prev.endpoints || []).filter((e) => e.id !== endpointId),
      };
      return exclusiveSkipsConvergence(updated)
        ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
        : updated;
    });
  };

  const updateNewEndpoint = (endpointId, field, value) => {
    setNewGateway((prev) => ({
      ...prev,
      endpoints: (prev.endpoints || []).map((e) =>
        e.id === endpointId ? { ...e, [field]: value } : e,
      ),
    }));
  };

  const saveNewGateway = () => {
    if (!newGateway) return;
    if (
      requiresConvergence(newGateway) &&
      !convergeValueOf(newGateway)
    ) {
      return;
    }
    const toSave = exclusiveSkipsConvergence(newGateway)
      ? { ...newGateway, ...EMPTY_CONVERGE_FIELDS }
      : newGateway;
    onGatewaysChange([...gateways, toSave]);
    setNewGateway(null);
    setShowAddPanel(false);
    setConfigStep(1);
  };

  const cancelAddGateway = () => {
    setNewGateway(null);
    setShowAddPanel(false);
    setConfigStep(1);
  };

  const removeGateway = (gatewayId) => {
    onGatewaysChange(gateways.filter((g) => g.id !== gatewayId));
  };

  const updateGateway = (gatewayId, field, value) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        const updated = { ...g, [field]: value };
        if (field === "type") {
          const n = normalizeType(value);
          if (n === "EXCLUSIVE" || n === "EVENT_BASED") {
            updated.convergeAtTaskId = "";
            updated.convergeGatewayName = "";
          }
          if (n === "PARALLEL") {
            updated.endpoints = [];
          }
          updated.type = n;
        }
        return updated;
      }),
    );
  };

  // Set a gateway's convergence target from the dropdown value:
  //  • "__END__"      → synchronize, then flow into the single End (convergeToEnd)
  //  • "__GW__<id>"   → join flows into that gateway's split (join → split)
  //  • any other value→ converge-at-task id
  const convergeFieldsFor = (value) => {
    if (value === "__END__")
      return { convergeToEnd: true, convergeAtTaskId: "", convergeAtGatewayId: "" };
    if (typeof value === "string" && value.startsWith("__GW__"))
      return {
        convergeToEnd: false,
        convergeAtTaskId: "",
        convergeAtGatewayId: value.slice(6),
      };
    return { convergeToEnd: false, convergeAtTaskId: value, convergeAtGatewayId: "" };
  };
  const convergeValueOf = (gw) =>
    gw?.convergeToEnd
      ? "__END__"
      : gw?.convergeAtGatewayId
        ? `__GW__${gw.convergeAtGatewayId}`
        : gw?.convergeAtTaskId || "";
  const setConvergeTarget = (gatewayId, value) => {
    onGatewaysChange(
      gateways.map((g) =>
        g.id !== gatewayId ? g : { ...g, ...convergeFieldsFor(value) },
      ),
    );
  };

  const updateBranch = (gatewayId, branchId, field, value) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        const updated = {
          ...g,
          branches: (g.branches || []).map((b) =>
            b.id === branchId ? { ...b, [field]: value } : b,
          ),
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  // Mark a single branch as the gateway's default path (BPMN 2.0: exactly one
  // default flow per data-based gateway; the default carries no condition).
  // Passing the already-default branch clears it (toggle off).
  const setDefaultBranch = (gatewayId, branchId) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        return {
          ...g,
          branches: (g.branches || []).map((b) => {
            const isDefault = b.id === branchId ? !b.isDefault : false;
            return { ...b, isDefault, condition: isDefault ? "" : b.condition };
          }),
        };
      }),
    );
  };

  const addBranch = (gatewayId) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        return {
          ...g,
          branches: [
            ...(g.branches || []),
            {
              id: createId(),
              targetTaskId: "",
              condition: "",
              isDefault: false,
              connectToEnd: false,
            },
          ],
        };
      }),
    );
  };

  const removeBranch = (gatewayId, branchId) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        if ((g.branches || []).length <= minBranchCount(g)) return g;
        const updated = {
          ...g,
          branches: (g.branches || []).filter((b) => b.id !== branchId),
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  // Add endpoint to existing gateway
  const addEndpoint = (gatewayId) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        const updated = {
          ...g,
          endpoints: [
            ...(g.endpoints || []),
            {
              id: createId(),
              endTaskId: "",
              endEventName: "",
              condition: "",
            },
          ],
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  // Remove endpoint from existing gateway
  const removeEndpoint = (gatewayId, endpointId) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        const updated = {
          ...g,
          endpoints: (g.endpoints || []).filter((e) => e.id !== endpointId),
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  // Update endpoint in existing gateway
  const updateEndpoint = (gatewayId, endpointId, field, value) => {
    onGatewaysChange(
      gateways.map((g) => {
        if (g.id !== gatewayId) return g;
        return {
          ...g,
          endpoints: (g.endpoints || []).map((e) =>
            e.id === endpointId ? { ...e, [field]: value } : e,
          ),
        };
      }),
    );
  };

  // Which gateway types can declare a converge (join) task.
  const needsConvergence = (type) => {
    const n = normalizeType(type);
    return (
      n === "PARALLEL" || n === "INCLUSIVE" || n === "EXCLUSIVE" || n === "COMPLEX"
    );
  };

  // Converge is required for merging types, except Exclusive with End + branch(es).
  const requiresConvergence = (gatewayOrType) => {
    if (gatewayOrType && typeof gatewayOrType === "object") {
      return gatewayRequiresConvergence(gatewayOrType, needsConvergence);
    }
    return needsConvergence(gatewayOrType);
  };

  const showsConvergence = (gateway) =>
    needsConvergence(gateway?.type) && !exclusiveSkipsConvergence(gateway);

  const needsConditions = (type) => {
    const n = normalizeType(type);
    return n === "EXCLUSIVE" || n === "INCLUSIVE" || n === "EVENT_BASED";
  };

  // BPMN 2.0: only data-based gateways may declare a default flow.
  const supportsDefault = (type) => {
    const n = normalizeType(type);
    return n === "EXCLUSIVE" || n === "INCLUSIVE" || n === "COMPLEX";
  };

  // Render configuration wizard
  const renderConfigWizard = () => {
    if (!newGateway) return null;
    const typeInfo = getTypeInfo(newGateway.type);
    const tasksAfter = getTasksAfter(newGateway.afterTaskId);
    const convergeOptions = getConvergeOptions(newGateway);

    return (
      <div className="gateway-wizard">
        {/* Progress Steps */}
        <div className="wizard-progress">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`wizard-step ${configStep >= step ? "active" : ""} ${configStep === step ? "current" : ""}`}
              onClick={() => step < configStep && setConfigStep(step)}
            >
              <div className="step-number">
                {configStep > step ? "✓" : step}
              </div>
              <div className="step-label">
                {step === 1
                  ? "Type & Placement"
                  : step === 2
                    ? "Configure Branches"
                    : "Review"}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Type & Placement */}
        {configStep === 1 && (
          <div className="wizard-content">
            <h4 className="step-title">Choose Gateway Type & Placement</h4>

            <div className="type-selection">
              {GATEWAY_TYPES.map((gt) => (
                <div
                  key={gt.id}
                  className={`type-card ${normalizeType(newGateway.type) === normalizeType(gt.value || gt.id) ? "selected" : ""}`}
                  style={{
                    "--type-color": gt.color,
                    "--type-bg": gt.bgColor,
                    "--type-border": gt.borderColor,
                  }}
                  onClick={() => updateNewGateway("type", gt.value || gt.id)}
                >
                  <div className="type-header">
                    <span className="type-symbol">{gt.symbol}</span>
                    <span className="type-label">{gt.label}</span>
                    <span className="type-short">({gt.shortLabel})</span>
                  </div>
                  <div className="type-description">{gt.description}</div>
                  <div className="type-example">💡 {gt.example}</div>
                </div>
              ))}
            </div>

            <div className="placement-section">
              <div className="field-group">
                <label className="field-label">
                  Gateway Name{" "}
                  <span className="field-optional">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newGateway.name}
                  onChange={(e) => updateNewGateway("name", e.target.value)}
                  placeholder="e.g., Is Approved?, Check Status"
                  className="field-input"
                />
              </div>

              <div className="field-group">
                <label className="field-label">
                  Place Gateway After <span className="field-required">*</span>
                </label>
                <select
                  value={newGateway.afterTaskId}
                  onChange={(e) =>
                    updateNewGateway("afterTaskId", e.target.value)
                  }
                  className="field-select"
                >
                  <option value="">-- Select placement --</option>
                  {getPlacementOptionsFor(newGateway).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.isStartEvent || t.isGateway
                        ? t.label
                        : `${t.order}. ${t.label}`}
                    </option>
                  ))}
                </select>
                <span className="field-hint">
                  The gateway will appear after the selected start event, task,
                  or subprocess
                </span>
              </div>

              {exclusiveSkipsConvergence(newGateway) && (
                <div
                  className="field-hint"
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    background: "var(--green-tint-50)",
                    border: "1px solid var(--green-tint-200)",
                    borderRadius: 6,
                    color: "var(--green-ink-800)",
                  }}
                >
                  No join needed: this Exclusive has continuing branch(es) and a
                  separate End path, so converge is cleared automatically.
                </div>
              )}

              {showsConvergence(newGateway) && (
                <div className="field-group">
                  <label
                    className="field-label"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span>
                      Converge At Task{" "}
                      <span className="field-badge">Required</span>
                    </span>
                    {!convergeValueOf(newGateway) && (
                      <button
                        type="button"
                        className="secondary-btn sm"
                        title="Pick the task right after where these branches naturally come back together (or End, if none continue)"
                        onClick={() =>
                          setNewGateway((prev) => ({
                            ...prev,
                            ...convergeFieldsFor(suggestConvergeTarget(prev)),
                          }))
                        }
                      >
                        Suggest join point
                      </button>
                    )}
                  </label>
                  <select
                    value={convergeValueOf(newGateway)}
                    onChange={(e) =>
                      setNewGateway((prev) => ({
                        ...prev,
                        ...convergeFieldsFor(e.target.value),
                      }))
                    }
                    className="field-select"
                    disabled={!newGateway.afterTaskId}
                  >
                    <option value="">-- Select convergence point --</option>
                    {convergeOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.order}. {t.label}
                      </option>
                    ))}
                    <option value="__END__">
                      ⏹ End Event — synchronize, then end
                    </option>
                    {gateways
                      .filter((g) => g.id !== newGateway.id)
                      .map((g) => (
                        <option key={g.id} value={`__GW__${g.id}`}>
                          ▷ Gateway: {g.name || g.type} — join into its split
                        </option>
                      ))}
                  </select>
                  <span className="field-hint">
                    {newGateway.convergeToEnd
                      ? "All paths synchronize at a join gateway, then flow into the single End event."
                      : newGateway.convergeAtGatewayId
                        ? "Branches synchronize at a join that flows directly into the chosen gateway’s split (join → split)."
                        : "All paths merge at this task. Choose “End Event” if the branches only finish the process, or a gateway to open a new split right after the join."}
                  </span>

                  <label className="field-label" style={{ marginTop: 12 }}>
                    Join Gateway Label{" "}
                    <span className="field-optional">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={newGateway.convergeGatewayName || ""}
                    onChange={(e) =>
                      updateNewGateway("convergeGatewayName", e.target.value)
                    }
                    placeholder="e.g., All checks done"
                    className="field-input"
                  />
                  <span className="field-hint">Label for the join gateway</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Configure Branches */}
        {configStep === 2 && (
          <div className="wizard-content">
            <h4 className="step-title">Configure Branches</h4>
            <p className="step-subtitle">
              {normalizeType(newGateway.type) === "EXCLUSIVE" &&
                "Only ONE path will execute based on conditions."}
              {normalizeType(newGateway.type) === "PARALLEL" &&
                "All paths execute simultaneously."}
              {normalizeType(newGateway.type) === "INCLUSIVE" &&
                "One or more paths can execute."}
              {normalizeType(newGateway.type) === "EVENT_BASED" &&
                "Waits for an external event."}
            </p>

            <div className="branches-list">
              {newGateway.branches.map((branch, idx) => {
                const usedTargetIds = new Set(
                  newGateway.branches
                    .filter(
                      (other) => other.id !== branch.id && other.targetTaskId,
                    )
                    .map((other) => String(other.targetTaskId)),
                );
                const isDuplicateTarget =
                  branch.targetTaskId &&
                  usedTargetIds.has(String(branch.targetTaskId));
                return (
                <div key={branch.id} className="branch-card">
                  <div className="branch-header">
                    <span
                      className="branch-number"
                      style={{ background: typeInfo.color }}
                    >
                      {idx + 1}
                    </span>
                    <span className="branch-title">Branch {idx + 1}</span>
                    {newGateway.branches.length > minBranchCount(newGateway) && (
                      <button
                        className="branch-remove"
                        onClick={() => removeBranchFromNew(branch.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="branch-fields">
                    <div className="field-group">
                      <label className="field-label">Target Task</label>
                      <select
                        value={branch.targetTaskId}
                        onChange={(e) =>
                          updateNewBranch(
                            branch.id,
                            "targetTaskId",
                            e.target.value,
                          )
                        }
                        className="field-select"
                        style={
                          isDuplicateTarget
                            ? { borderColor: "#ef4444", background: "var(--red-tint-50)" }
                            : undefined
                        }
                      >
                        <option value="">
                          {branch.endEventName
                            ? `⏹ End Event: ${branch.endEventName}`
                            : "⏹ End Event (shared End)"}
                        </option>
                        {tasksAfter.map((t) => (
                          <option
                            key={t.id}
                            value={t.id}
                            disabled={usedTargetIds.has(String(t.id))}
                          >
                            {t.order}. {t.label}
                            {usedTargetIds.has(String(t.id))
                              ? " (already used)"
                              : ""}
                          </option>
                        ))}
                        {gateways
                          .filter((g) => g.id !== newGateway.id)
                          .map((g) => (
                            <option
                              key={g.id}
                              value={`__GW__${g.id}`}
                              disabled={usedTargetIds.has(`__GW__${g.id}`)}
                            >
                              ▷ Gateway: {g.name || g.type}
                              {usedTargetIds.has(`__GW__${g.id}`)
                                ? " (already used)"
                                : ""}
                            </option>
                          ))}
                      </select>
                      {isDuplicateTarget && (
                        <span
                          className="field-hint"
                          style={{ color: "var(--red-ink-600)" }}
                        >
                          Another branch already routes here — pick a
                          different target.
                        </span>
                      )}
                    </div>
                    {needsConditions(newGateway.type) && (
                      <div className="field-group">
                        <label className="field-label">Condition</label>
                        <input
                          type="text"
                          value={branch.condition}
                          onChange={(e) =>
                            updateNewBranch(
                              branch.id,
                              "condition",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., approved == true"
                          className="field-input"
                        />
                      </div>
                    )}
                    {/* Target = End Event: optional own named End event */}
                    {!branch.targetTaskId &&
                      normalizeType(newGateway.type) !== "PARALLEL" && (
                        <div className="field-group" style={{ marginTop: 4 }}>
                          <input
                            type="text"
                            value={branch.endEventName || ""}
                            onChange={(e) =>
                              updateNewBranch(
                                branch.id,
                                "endEventName",
                                e.target.value,
                              )
                            }
                            placeholder="End event name (optional) — e.g. Rejected"
                            title="Name this to give the branch its OWN End event. Leave blank to use the shared End."
                            className="field-input"
                          />
                          <span
                            style={{ fontSize: 11, color: "var(--slate-400)" }}
                          >
                            blank = shared End · named = its own End event
                          </span>
                        </div>
                      )}
                  </div>
                </div>
                );
              })}
            </div>

            <button className="add-branch-btn" onClick={addBranchToNew}>
              + Add Another Branch
            </button>

            {normalizeType(newGateway.type) !== "PARALLEL" && (
              <div className="endpoints-section">
                <div className="endpoints-header">
                  <h5 className="endpoints-title">End Events (Optional)</h5>
                  <button
                    className="add-endpoint-btn"
                    onClick={addEndpointToNew}
                  >
                    + Add End Event
                  </button>
                </div>
                <p className="endpoints-hint">
                  Named end points that terminate the process (e.g., "Rejected",
                  "Cancelled")
                </p>

                {(newGateway.endpoints || []).map((endpoint, idx) => (
                  <div key={endpoint.id} className="endpoint-card">
                    <div className="endpoint-header">
                      <span className="endpoint-icon">●</span>
                      <span className="endpoint-title">
                        End Event {idx + 1}
                      </span>
                      <button
                        className="endpoint-remove"
                        onClick={() => removeEndpointFromNew(endpoint.id)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="endpoint-fields">
                      <div className="field-group">
                        <label className="field-label">End Event Name</label>
                        <input
                          type="text"
                          value={endpoint.endEventName}
                          onChange={(e) =>
                            updateNewEndpoint(
                              endpoint.id,
                              "endEventName",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., Rejected"
                          className="field-input"
                        />
                      </div>
                      {needsConditions(newGateway.type) && (
                        <div className="field-group">
                          <label className="field-label">Condition</label>
                          <input
                            type="text"
                            value={endpoint.condition}
                            onChange={(e) =>
                              updateNewEndpoint(
                                endpoint.id,
                                "condition",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., rejected == true"
                            className="field-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {configStep === 3 && (
          <div className="wizard-content">
            <h4 className="step-title">Review Configuration</h4>

            <div className="review-card">
              <div className="review-header">
                <span
                  className="review-symbol"
                  style={{ background: typeInfo.color }}
                >
                  {typeInfo.symbol}
                </span>
                <div className="review-info">
                  <span className="review-name">
                    {newGateway.name || typeInfo.label + " Gateway"}
                  </span>
                  <span className="review-type">{typeInfo.label}</span>
                </div>
              </div>

              <div className="review-details">
                <div className="review-row">
                  <span className="review-label">After Task/Gateway/Event:</span>
                  <span className="review-value">
                    {getPlacementLabel(newGateway.afterTaskId)}
                  </span>
                </div>
                {showsConvergence(newGateway) && (
                  <div className="review-row">
                    <span className="review-label">Converge At:</span>
                    <span className="review-value">
                      {newGateway.convergeToEnd
                        ? "⏹ End Event (synchronize, then end)"
                        : newGateway.convergeAtGatewayId
                          ? `▷ ${
                              gateways.find(
                                (g) => g.id === newGateway.convergeAtGatewayId,
                              )?.name || "Gateway"
                            } (join → split)`
                          : taskOptions.find(
                              (t) => t.id === newGateway.convergeAtTaskId,
                            )?.label || "Not selected"}
                    </span>
                  </div>
                )}
                {showsConvergence(newGateway) && (
                  <div className="review-row">
                    <span className="review-label">Join Gateway Label:</span>
                    <span className="review-value">
                      {newGateway.convergeGatewayName || "Not provided"}
                    </span>
                  </div>
                )}
                {exclusiveSkipsConvergence(newGateway) && (
                  <div className="review-row">
                    <span className="review-label">Converge At:</span>
                    <span className="review-value">
                      Not required (Exclusive with End path)
                    </span>
                  </div>
                )}
              </div>

              <div className="review-branches">
                <span className="review-section-title">
                  Branches ({newGateway.branches.length})
                </span>
                {newGateway.branches.map((branch, idx) => (
                  <div key={branch.id} className="review-branch">
                    <span className="review-branch-num">{idx + 1}</span>
                    <span className="review-branch-target">
                      →{" "}
                      {taskOptions.find((t) => t.id === branch.targetTaskId)
                        ?.label || "Not selected"}
                    </span>
                    {branch.condition && (
                      <span className="review-branch-condition">
                        when: {branch.condition}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {(newGateway.endpoints || []).length > 0 &&
                normalizeType(newGateway.type) !== "PARALLEL" && (
                  <div className="review-endpoints">
                    <span className="review-section-title">End Events</span>
                    {newGateway.endpoints.map((ep) => (
                      <div key={ep.id} className="review-endpoint">
                        <span className="review-endpoint-icon">●</span>
                        <span className="review-endpoint-name">
                          {ep.endEventName}
                        </span>
                        {ep.condition && (
                          <span className="review-endpoint-condition">
                            when: {ep.condition}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="wizard-footer">
          <button className="wizard-btn cancel" onClick={cancelAddGateway}>
            Cancel
          </button>
          <div className="wizard-nav">
            {configStep > 1 && (
              <button
                className="wizard-btn back"
                onClick={() => setConfigStep((s) => s - 1)}
              >
                ← Back
              </button>
            )}
            {configStep < 3 ? (
              <button
                className="wizard-btn next"
                onClick={() => setConfigStep((s) => s + 1)}
                disabled={
                  (configStep === 1 && !newGateway.afterTaskId) ||
                  (configStep === 1 &&
                    requiresConvergence(newGateway) &&
                    !convergeValueOf(newGateway))
                }
              >
                Next →
              </button>
            ) : (
              <button
                className="wizard-btn save"
                onClick={saveNewGateway}
                disabled={
                  requiresConvergence(newGateway) &&
                  !convergeValueOf(newGateway)
                }
              >
                ✓ Add Gateway
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render gateway card
  const renderGatewayCard = (gateway, index) => {
    const typeInfo = getTypeInfo(gateway.type);
    const isExpanded = expandedGateway === gateway.id;
    const tasksAfter = getTasksAfter(gateway.afterTaskId);
    const afterTask = { label: getPlacementLabel(gateway.afterTaskId) };
    const convergeOptions = getConvergeOptions(gateway);

    return (
      <div
        key={gateway.id}
        className={`gateway-card ${isExpanded ? "expanded" : ""}`}
      >
        <div
          className="gateway-card-header"
          onClick={() => setExpandedGateway(isExpanded ? null : gateway.id)}
        >
          <div className="gateway-card-left">
            <span
              className="gateway-symbol"
              style={{ background: typeInfo.color }}
            >
              {typeInfo.symbol}
            </span>
            <div className="gateway-info">
              <span className="gateway-name">
                {gateway.name || `Gateway ${index + 1}`}
              </span>
              <span className="gateway-meta">
                {typeInfo.label} • After: {afterTask?.label || "?"} •{" "}
                {(gateway.branches || []).length} branches
              </span>
            </div>
          </div>
          <div className="gateway-card-actions">
            <button
              className="gateway-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedGateway(isExpanded ? null : gateway.id);
              }}
            >
              {isExpanded ? "▲" : "▼"}
            </button>
            <button
              className="gateway-action-btn remove"
              onClick={(e) => {
                e.stopPropagation();
                removeGateway(gateway.id);
              }}
            >
              ×
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="gateway-card-body">
            <div className="gateway-settings">
              <div className="field-row">
                <div className="field-group">
                  <label className="field-label">Type</label>
                  <select
                    value={gateway.type}
                    onChange={(e) =>
                      updateGateway(gateway.id, "type", e.target.value)
                    }
                    className="field-select"
                  >
                    {GATEWAY_TYPES.map((gt) => (
                      <option key={gt.value || gt.id} value={gt.value || gt.id}>
                        {gt.symbol} {gt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label">Name</label>
                  <input
                    type="text"
                    value={gateway.name || ""}
                    onChange={(e) =>
                      updateGateway(gateway.id, "name", e.target.value)
                    }
                    placeholder="Optional name"
                    className="field-input"
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">After Task/Gateway/Event</label>
                  <select
                    value={gateway.afterTaskId || ""}
                    onChange={(e) =>
                      updateGateway(gateway.id, "afterTaskId", e.target.value)
                    }
                    className="field-select"
                  >
                    <option value="">Select...</option>
                    {getPlacementOptionsFor(gateway).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.isStartEvent || t.isGateway
                        ? t.label
                        : `${t.order}. ${t.label}`}
                      </option>
                    ))}
                  </select>
                </div>
                {exclusiveSkipsConvergence(gateway) && (
                  <div
                    className="field-hint"
                    style={{
                      marginBottom: 12,
                      padding: "8px 10px",
                      background: "var(--green-tint-50)",
                      border: "1px solid var(--green-tint-200)",
                      borderRadius: 6,
                      color: "var(--green-ink-800)",
                    }}
                  >
                    No join needed: this Exclusive has continuing branch(es) and
                    a separate End path, so converge is cleared automatically.
                  </div>
                )}
                {showsConvergence(gateway) && (
                  <div className="field-group">
                    <label
                      className="field-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span>
                        Converge At Task{" "}
                        <span style={{ color: "var(--red-ink-600)" }}>(required)</span>
                      </span>
                      {!convergeValueOf(gateway) && (
                        <button
                          type="button"
                          className="secondary-btn sm"
                          title="Pick the task right after where these branches naturally come back together (or End, if none continue)"
                          onClick={() =>
                            setConvergeTarget(
                              gateway.id,
                              suggestConvergeTarget(gateway),
                            )
                          }
                        >
                          Suggest join point
                        </button>
                      )}
                    </label>
                    <select
                      value={convergeValueOf(gateway)}
                      onChange={(e) =>
                        setConvergeTarget(gateway.id, e.target.value)
                      }
                      className="field-select"
                    >
                      <option value="">Select...</option>
                      {convergeOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.order}. {t.label}
                        </option>
                      ))}
                      <option value="__END__">
                        ⏹ End Event — synchronize, then end
                      </option>
                      {gateways
                        .filter((g) => g.id !== gateway.id)
                        .map((g) => (
                          <option key={g.id} value={`__GW__${g.id}`}>
                            ▷ Gateway: {g.name || g.type} — join into its split
                          </option>
                        ))}
                    </select>
                    <span style={{ fontSize: 11, color: "var(--slate-400)" }}>
                      {gateway.convergeToEnd
                        ? "Branches synchronize at a join of the same type, then flow into the single End event."
                        : gateway.convergeAtGatewayId
                          ? "Branches synchronize at a join that flows directly into the chosen gateway’s split (join → split)."
                          : "Pick the task where paths merge, “End Event” if they only finish the process, or another gateway for join → split."}
                    </span>
                  </div>
                )}
                {showsConvergence(gateway) && (
                  <div className="field-group">
                    <label className="field-label">Join Gateway Label</label>
                    <input
                      type="text"
                      value={gateway.convergeGatewayName || ""}
                      onChange={(e) =>
                        updateGateway(
                          gateway.id,
                          "convergeGatewayName",
                          e.target.value,
                        )
                      }
                      placeholder="e.g., All checks done"
                      className="field-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Branches */}
            <div className="gateway-branches">
              <div className="branches-header">
                <span className="branches-title">Branches</span>
                <button
                  className="add-btn"
                  onClick={() => addBranch(gateway.id)}
                >
                  + Add Branch
                </button>
              </div>
              {supportsDefault(gateway.type) && (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--slate-400)",
                    padding: "2px 8px 6px",
                    lineHeight: 1.4,
                  }}
                >
                  Mark one branch <strong>Default</strong> (taken when no
                  condition matches). To loop back, set a branch target to an
                  earlier task — a branch cannot point at the Start event (BPMN
                  start events take no incoming flow).
                </div>
              )}
              {(gateway.branches || []).map((branch, brIdx) => {
                const usedTargetIds = new Set(
                  (gateway.branches || [])
                    .filter(
                      (other) => other.id !== branch.id && other.targetTaskId,
                    )
                    .map((other) => String(other.targetTaskId)),
                );
                const isDuplicateTarget =
                  branch.targetTaskId &&
                  usedTargetIds.has(String(branch.targetTaskId));
                return (
                <div key={branch.id}>
                  <div className="branch-row">
                    <span className="branch-num">{brIdx + 1}</span>
                    <select
                      value={branch.targetTaskId || ""}
                      onChange={(e) =>
                        updateBranch(
                          gateway.id,
                          branch.id,
                          "targetTaskId",
                          e.target.value,
                        )
                      }
                      className="field-select compact"
                      style={
                        isDuplicateTarget
                          ? { borderColor: "#ef4444", background: "var(--red-tint-50)" }
                          : undefined
                      }
                    >
                      <option value="">
                        {branch.endEventName
                          ? `⏹ End Event: ${branch.endEventName}`
                          : "⏹ End Event (shared End)"}
                      </option>
                      {tasksAfter.map((t) => (
                        <option
                          key={t.id}
                          value={t.id}
                          disabled={usedTargetIds.has(String(t.id))}
                        >
                          {t.order}. {t.label}
                          {usedTargetIds.has(String(t.id))
                            ? " (already used)"
                            : ""}
                        </option>
                      ))}
                      {gateways
                        .filter((g) => g.id !== gateway.id)
                        .map((g) => (
                          <option
                            key={g.id}
                            value={`__GW__${g.id}`}
                            disabled={usedTargetIds.has(`__GW__${g.id}`)}
                          >
                            ▷ Gateway: {g.name || g.type}
                            {usedTargetIds.has(`__GW__${g.id}`)
                              ? " (already used)"
                              : ""}
                          </option>
                        ))}
                    </select>
                    {isDuplicateTarget && (
                      <span
                        title="Another branch already routes to this same target"
                        style={{
                          fontSize: 11,
                          color: "var(--red-ink-600)",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        ⚠ duplicate
                      </span>
                    )}
                    {needsConditions(gateway.type) && (
                      <input
                        type="text"
                        value={branch.isDefault ? "" : branch.condition || ""}
                        disabled={!!branch.isDefault}
                        onChange={(e) =>
                          updateBranch(
                            gateway.id,
                            branch.id,
                            "condition",
                            e.target.value,
                          )
                        }
                        placeholder={
                          branch.isDefault ? "default path" : "Condition..."
                        }
                        className="field-input compact"
                      />
                    )}
                    {supportsDefault(gateway.type) && (
                      <label
                        title="Default path — taken when no other branch condition matches (BPMN default flow)"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          cursor: "pointer",
                          userSelect: "none",
                          fontSize: 11,
                          fontWeight: 600,
                          color: branch.isDefault ? "var(--accent-ink-600)" : "var(--slate-400)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="radio"
                          name={`default-branch-${gateway.id}`}
                          checked={!!branch.isDefault}
                          onClick={() => setDefaultBranch(gateway.id, branch.id)}
                          onChange={() => {}}
                          style={{ cursor: "pointer", accentColor: "var(--accent-600)" }}
                        />
                        Default
                      </label>
                    )}
                    <button
                      className="remove-btn"
                      onClick={() => removeBranch(gateway.id, branch.id)}
                      disabled={
                        (gateway.branches || []).length <=
                        minBranchCount(gateway)
                      }
                    >
                      ×
                    </button>
                  </div>
                  {/* Target = End Event: let this branch have its OWN named end
                      event (a separate terminator, e.g. "Rejected") or use the
                      shared End when left blank. */}
                  {!branch.targetTaskId &&
                    normalizeType(gateway.type) !== "PARALLEL" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          paddingLeft: 8,
                          marginTop: 4,
                          marginBottom: 2,
                        }}
                      >
                        <input
                          type="text"
                          value={branch.endEventName || ""}
                          onChange={(e) =>
                            updateBranch(
                              gateway.id,
                              branch.id,
                              "endEventName",
                              e.target.value,
                            )
                          }
                          placeholder="End event name (optional) — e.g. Rejected"
                          title="Name this to give the branch its OWN End event (a separate terminator). Leave blank to use the process's shared End."
                          className="field-input compact"
                          style={{ maxWidth: 300 }}
                        />
                        <span
                          style={{ fontSize: 10, color: "var(--slate-400)" }}
                        >
                          blank = shared End · named = its own End
                        </span>
                      </div>
                    )}
                </div>
                );
              })}
            </div>

            {normalizeType(gateway.type) !== "PARALLEL" && (
              <div className="gateway-endpoints">
                <div className="endpoints-header">
                  <span className="endpoints-title">End Events</span>
                  <button
                    className="add-btn"
                    onClick={() => addEndpoint(gateway.id)}
                  >
                    + Add End Event
                  </button>
                </div>
                {(gateway.endpoints || []).map((ep, epIdx) => (
                  <div key={ep.id} className="endpoint-row">
                    <span className="endpoint-num">●</span>
                    <select
                      value={ep.endTaskId || ""}
                      onChange={(e) =>
                        updateEndpoint(
                          gateway.id,
                          ep.id,
                          "endTaskId",
                          e.target.value,
                        )
                      }
                      className="field-select compact"
                    >
                      <option value="">After (same as gateway)</option>
                      {taskOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.order}. {t.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={ep.endEventName || ""}
                      onChange={(e) =>
                        updateEndpoint(
                          gateway.id,
                          ep.id,
                          "endEventName",
                          e.target.value,
                        )
                      }
                      placeholder="End Event Name..."
                      className="field-input compact"
                    />
                    {needsConditions(gateway.type) && (
                      <input
                        type="text"
                        value={ep.condition || ""}
                        onChange={(e) =>
                          updateEndpoint(
                            gateway.id,
                            ep.id,
                            "condition",
                            e.target.value,
                          )
                        }
                        placeholder="Condition..."
                        className="field-input compact"
                      />
                    )}
                    <button
                      className="remove-btn"
                      onClick={() => removeEndpoint(gateway.id, ep.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(gateway.endpoints || []).length === 0 && (
                  <div className="no-endpoints">No end events configured</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="gateway-section">
      <div className="gateway-section-header">
        <div className="header-left">
          <span className="header-icon">⬡</span>
          <div className="header-text">
            <h3 className="header-title">Gateways</h3>
            <p className="header-subtitle">
              Control how your workflow branches and merges
            </p>
          </div>
        </div>
        {!showAddPanel && (
          <button
            className="add-gateway-btn"
            onClick={() => startAddGateway()}
            disabled={taskOptions.length === 0}
          >
            + Add Gateway
          </button>
        )}
      </div>

      {taskOptions.length === 0 && (
        <div className="no-tasks-warning">
          <span className="warning-icon">⚠️</span> Add tasks to the workflow
          first before configuring gateways.
        </div>
      )}

      {showAddPanel && taskOptions.length > 0 && (
        <div className="add-gateway-panel">
          {!newGateway ? (
            <>
              <h4 className="panel-title">Choose a Pattern or Start Fresh</h4>
              <div className="pattern-grid">
                {QUICK_PATTERNS.map((pattern) => {
                  const typeInfo = getTypeInfo(pattern.type);
                  return (
                    <div
                      key={pattern.id}
                      className="pattern-card"
                      onClick={() => startAddGateway(pattern)}
                    >
                      <span className="pattern-icon">{pattern.icon}</span>
                      <span className="pattern-name">{pattern.name}</span>
                      <span className="pattern-desc">
                        {pattern.description}
                      </span>
                      <span
                        className="pattern-type"
                        style={{ color: typeInfo.color }}
                      >
                        {typeInfo.symbol} {typeInfo.label}
                      </span>
                    </div>
                  );
                })}
                <div
                  className="pattern-card custom"
                  onClick={() => startAddGateway()}
                >
                  <span className="pattern-icon">✎</span>
                  <span className="pattern-name">Custom Gateway</span>
                  <span className="pattern-desc">Start from scratch</span>
                </div>
              </div>
              <button className="cancel-btn" onClick={cancelAddGateway}>
                Cancel
              </button>
            </>
          ) : (
            renderConfigWizard()
          )}
        </div>
      )}

      {gateways.length > 0 && !showAddPanel && (
        <div className="gateways-list">
          {gateways.map((gw, idx) => renderGatewayCard(gw, idx))}
        </div>
      )}

      {gateways.length === 0 && !showAddPanel && taskOptions.length > 0 && (
        <div className="empty-state">
          <div className="empty-icon">⬡</div>
          <div className="empty-text">No gateways configured</div>
          <div className="empty-hint">
            Click "Add Gateway" to control workflow branching
          </div>
        </div>
      )}

      {showPreview && gateways.length > 0 && onPreviewRequest && (
        <button className="preview-btn" onClick={onPreviewRequest}>
          👁 Preview BPMN Diagram
        </button>
      )}
    </div>
  );
}

export default GatewaySection;
